import { useEffect, useState } from "react";

import {
  ApiError,
  apiClient,
  type CapabilityCatalog,
  type EffectiveCapability,
} from "../api/client";

export function CapabilityCenterPage() {
  const [catalog, setCatalog] = useState<CapabilityCatalog | null>(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    apiClient
      .getCapabilityCatalog()
      .then((value) => active && setCatalog(value))
      .catch((caught: unknown) => {
        if (active) setError(caught instanceof ApiError ? caught.message : "暂时无法加载能力目录");
      });
    return () => {
      active = false;
    };
  }, []);

  return (
    <div className="page-stack capability-center-page">
      <header className="flow-header capability-center-heading">
        <div>
          <span className="eyebrow">当前账号的有效发布能力</span>
          <h1>能力中心</h1>
          <p>查看可用 Skill 与固定 Workflow 的输入、输出、资源范围及 Agent 使用方式。</p>
        </div>
        <span className="phase-badge">只读目录</span>
      </header>

      <div className="catalog-banner readonly" role="note">
        能力定义由平台发布并保持不可变；当前版本不提供客户创建、编辑、上传或发布入口。
      </div>
      {error && (
        <div className="catalog-banner error" role="alert">
          {error}
        </div>
      )}
      {!catalog && !error && <div className="empty-page">正在加载授权范围内的能力…</div>}
      {catalog && (
        <>
          <CapabilitySection
            capabilities={catalog.skills}
            eyebrow="封闭输入输出合同"
            title="Skills"
          />
          <CapabilitySection
            capabilities={catalog.workflows}
            eyebrow="固定版本执行图"
            title="Workflows"
          />
        </>
      )}
    </div>
  );
}

function CapabilitySection({
  capabilities,
  eyebrow,
  title,
}: {
  capabilities: EffectiveCapability[];
  eyebrow: string;
  title: string;
}) {
  return (
    <section aria-labelledby={`capability-${title.toLowerCase()}`}>
      <div className="section-heading">
        <div>
          <span className="eyebrow">{eyebrow}</span>
          <h2 id={`capability-${title.toLowerCase()}`}>{title}</h2>
        </div>
        <span className="phase-badge">{capabilities.length} 项</span>
      </div>
      <div className="capability-grid">
        {capabilities.map((capability) => (
          <CapabilityCard
            capability={capability}
            key={`${capability.code}@${capability.version}`}
          />
        ))}
        {!capabilities.length && <div className="empty-page">当前资源授权下没有可用 {title}。</div>}
      </div>
    </section>
  );
}

function CapabilityCard({ capability }: { capability: EffectiveCapability }) {
  const usage = capability.intended_usage === "direct" ? "可直接使用" : "Agent 内部使用";
  return (
    <article className="capability-card">
      <div className="capability-card-heading">
        <div>
          <span className="task-type">
            {capability.kind} · v{capability.version}
          </span>
          <h3>{capability.display_name}</h3>
        </div>
        <span className="phase-badge">{usage}</span>
      </div>
      <p>{capability.description}</p>
      <dl className="capability-metadata">
        <div>
          <dt>代码</dt>
          <dd>{capability.code}</dd>
        </div>
        <div>
          <dt>资源范围</dt>
          <dd>{capability.required_resource_scopes.join("、")}</dd>
        </div>
        <div>
          <dt>Agent</dt>
          <dd>
            {capability.used_by_agents.length ? capability.used_by_agents.join("、") : "无固定引用"}
          </dd>
        </div>
      </dl>
      <details>
        <summary>查看输入与输出合同</summary>
        <div className="capability-schemas">
          <SchemaBlock label="Input" schema={capability.input_schema} />
          <SchemaBlock label="Output" schema={capability.output_schema} />
        </div>
      </details>
    </article>
  );
}

function SchemaBlock({ label, schema }: { label: string; schema: Record<string, unknown> }) {
  return (
    <div>
      <strong>{label}</strong>
      <pre>{JSON.stringify(schema, null, 2)}</pre>
    </div>
  );
}
